import { Enrollment, Field, FieldGeometry, ImageryExtent } from "@/types/field";
import { useParams } from "@tanstack/react-router";
import { ReactNode, useEffect, useMemo, useState } from "react";
import {
    ADVANCED_COMPLETE_OPTIONS,
    getImageryTypeOptions,
    Imagery,
    ImageryType,
    IRRIGATION_OPTIONS,
} from "@/types/imagery";
import { CropCycle, CropCycleV2 } from "@/types/cropCycle";
import dayjs from "dayjs";
import FieldDashboardContext from "./FieldDashboardContext";
import useRequest from "../request/useRequest";
import {
    Coordinates,
    invertedTurfBboxToFourCornerCoordinates,
} from "../utils/map";
import { bbox } from "@turf/turf";
import { EnrollmentTier } from "../utils/enrollment";
import useFeatureFlag from "../featureFlags/useFeatureFlags";
import useAuth from "../auth/useAuth";
import { Resource } from "@/types/user";
import { NaveWaterEstimate } from "@/types/water";
import { getTodayWithTimeTravel } from "../utils/date";

function FieldDashboardProvider({ children }: { children: ReactNode }) {
    const { useGetRequest } = useRequest();
    const { cropCyclesV2Enabled, irrigationEnabled } = useFeatureFlag();
    const { isAtLeastSentinel } = useAuth();
    const { fieldId } = useParams({
        strict: false,
    });
    const fieldQuery = useGetRequest<Field>({
        url: `fields/${fieldId}/`,
        enabled: !!fieldId,
    });
    const [selectedCropCycle, setSelectedCropCycle] = useState<
        CropCycle | CropCycleV2 | null
    >(null);
    const [selectedImage, setSelectedImage] = useState<Imagery | undefined>();

    const geometryQuery = useGetRequest<FieldGeometry>({
        url: `fields/${fieldId}/geometry/`,
        enabled: !!fieldId,
    });

    const cropCycleQuery = useGetRequest<CropCycle[]>({
        url: `fields/${fieldId}/crop_cycles/`,
        enabled: !!fieldId,
    });

    const cropCycleQueryV2 = useGetRequest<CropCycleV2[]>({
        url: `fields/${fieldId}/crop_cycles_v2/`,
        enabled: !!fieldId,
    });

    const fieldImageryQuery = useGetRequest<Imagery[]>({
        url: `fields/${fieldId}/imagery/`,
        enabled: !!fieldId && !!selectedCropCycle,
    });

    const fieldEnrollmentQuery = useGetRequest<Enrollment[]>({
        url: `fields/${fieldId}/enrollments/`,
        enabled: !!fieldId,
    });

    const [selectedDate, setSelectedDate] = useState<string | undefined>();

    const imageryOptions = useMemo(() => {
        if (!selectedDate) return [];
        const imageDate = dayjs(selectedDate);
        const enrollmentForDate = fieldEnrollmentQuery?.data?.find(
            (enrollment) =>
                dayjs(enrollment.start_date).isSameOrBefore(imageDate) &&
                dayjs(enrollment.end_date).isSameOrAfter(imageDate),
        );

        // If we don't have an enrollment but have a date, it's from pre-2025 enrollment work, so return everything
        if (!enrollmentForDate) {
            if (irrigationEnabled && isAtLeastSentinel(Resource.IRRIGATION)) {
                return [...ADVANCED_COMPLETE_OPTIONS, ...IRRIGATION_OPTIONS];
            }
            return ADVANCED_COMPLETE_OPTIONS;
        }
        const imageryOptions = getImageryTypeOptions(
            enrollmentForDate?.enrollment_tier as EnrollmentTier,
        );
        if (irrigationEnabled && isAtLeastSentinel(Resource.IRRIGATION)) {
            return [...imageryOptions, ...IRRIGATION_OPTIONS];
        }
        return imageryOptions;
    }, [
        fieldEnrollmentQuery?.data,
        irrigationEnabled,
        isAtLeastSentinel,
        selectedDate,
    ]);

    useEffect(() => {
        if (selectedCropCycle) return;
        if (!cropCyclesV2Enabled) {
            if (cropCycleQuery?.data) {
                setSelectedCropCycle(
                    cropCycleQuery?.data?.find(
                        (cycle) => cycle.start_date && !cycle.end_date,
                    ) || null,
                );
            }
        } else {
            if (cropCycleQueryV2?.data?.length) {
                const cyclesWithPlantingInPast = cropCycleQueryV2?.data?.filter(
                    (cycle) =>
                        dayjs(
                            cycle.effective_planting_operation.start_date,
                        ).isBefore(getTodayWithTimeTravel()),
                );
                if (cyclesWithPlantingInPast.length == 0) {
                    setSelectedCropCycle(
                        cropCycleQueryV2?.data?.[
                            cropCycleQueryV2?.data?.length - 1
                        ],
                    );
                } else {
                    setSelectedCropCycle(
                        cyclesWithPlantingInPast[
                            cyclesWithPlantingInPast.length - 1
                        ] || null,
                    );
                }
            }
        }
    }, [
        cropCycleQuery?.data,
        cropCycleQueryV2?.data,
        cropCyclesV2Enabled,
        selectedCropCycle,
    ]);

    const filteredImagery = useMemo(() => {
        const mostRecentImagesMap: Map<string, Imagery> = new Map<
            string,
            Imagery
        >();
        fieldImageryQuery?.data?.forEach((image: Imagery) => {
            const imageCaptureDateTime = dayjs(image.capture_date);
            const imageCaptureDateString =
                imageCaptureDateTime.format("YYYY-MM-DD");

            const existingImage = mostRecentImagesMap.get(
                imageCaptureDateString,
            );

            if (image.active_for_day || !existingImage) {
                mostRecentImagesMap.set(imageCaptureDateString, image);
            }
        });

        return Array.from(mostRecentImagesMap.values());
    }, [fieldImageryQuery?.data]);

    const fieldBbox = useMemo(() => {
        if (!fieldQuery?.data || !geometryQuery?.data) return;

        return invertedTurfBboxToFourCornerCoordinates(
            bbox(geometryQuery.data.field),
        ) as Coordinates;
    }, [fieldQuery?.data, geometryQuery?.data]);

    const imageryBbox = useMemo(() => {
        if (!fieldQuery?.data || !geometryQuery?.data) return;

        if (
            fieldQuery.data.imagery_extent === ImageryExtent.FIELD ||
            !geometryQuery.data.irrigation_systems.features?.length
        ) {
            return fieldBbox;
        } else {
            return invertedTurfBboxToFourCornerCoordinates(
                bbox(geometryQuery.data?.irrigation_systems.features[0]),
            );
        }
    }, [fieldQuery?.data, geometryQuery?.data, fieldBbox]) as
        | Coordinates
        | undefined;

    const fieldIsAdvanced = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;

        return [
            EnrollmentTier.Advanced,
            EnrollmentTier.Basic,
            EnrollmentTier.AdvancedV2,
        ].includes(
            fieldQuery?.data?.active_enrollment
                ?.enrollment_tier as EnrollmentTier,
        );
    }, [fieldQuery?.data]);

    const fieldIsStandard = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            fieldQuery?.data?.active_enrollment?.enrollment_tier ===
            EnrollmentTier.Standard
        );
    }, [fieldQuery?.data]);

    const fieldIsComplete = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            fieldQuery?.data?.active_enrollment?.enrollment_tier ===
            EnrollmentTier.Complete
        );
    }, [fieldQuery?.data]);

    const fieldHasIrrigation = useMemo(() => {
        if (!fieldQuery?.data?.active_enrollment) return false;
        return (
            [EnrollmentTier.Irrigation, EnrollmentTier.Complete].includes(
                fieldQuery?.data?.active_enrollment
                    ?.enrollment_tier as EnrollmentTier,
            ) && irrigationEnabled
        );
    }, [fieldQuery?.data, irrigationEnabled]);

    const fieldWaterQuery = useGetRequest<NaveWaterEstimate[]>({
        url: `fields/${fieldId}/water/`,
        enabled: !!fieldId && fieldHasIrrigation,
    });

    const [imageType, setImageType] = useState<ImageryType>(ImageryType.NDRE);

    return (
        <FieldDashboardContext.Provider
            value={{
                fieldId,
                cropCycleQuery,
                cropCycleQueryV2,
                selectedCropCycle,
                setSelectedCropCycle,
                fieldImageryQuery,
                fieldQuery,
                filteredImagery,
                setSelectedImage,
                selectedImage,
                geometryQuery,
                imageryBbox,
                fieldIsAdvanced,
                fieldIsStandard,
                fieldIsComplete,
                imageType,
                setImageType,
                fieldBbox,
                fieldEnrollmentQuery,
                imageryOptions,
                fieldHasIrrigation,
                fieldWaterQuery,
                selectedDate,
                setSelectedDate,
            }}
        >
            {children}
        </FieldDashboardContext.Provider>
    );
}

export default FieldDashboardProvider;
